import {
  Children,
  type MouseEvent,
  ReactElement,
  isValidElement,
  useState,
} from 'react';

import {
  Button,
  ButtonProps,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import classNames from 'classnames';

import { useLocalStorage } from '../../hooks/useLocalStorage';

export interface OptionProps {
  label: string;
  disabled?: boolean;
  onClick?: () => void; // Not needed for submit/reset buttons in forms
  tooltip?: string; // Tooltip when hovering over the button with the selected option
  description?: string; // Description of the option in the menu
  value?: string; // Value to pass when used in forms
  type?: 'button' | 'submit' | 'reset'; // Button type for form submission
}

export interface SplitButtonOptionProps extends Omit<OptionProps, 'label'> {
  children: string;
}

export interface SplitButtonProps
  extends Omit<ButtonProps, 'onClick' | 'children'> {
  children?:
    | ReactElement<SplitButtonOptionProps>
    | ReactElement<SplitButtonOptionProps>[];
  id: string;
  defaultSelectedIndex?: number;
  ariaLabel?: string;
}

// Not using ButtonGroup because it's styled differently than regular buttons in the Encore themes
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'stretch', // Changed to stretch to ensure equal heights
    gap: 1,
  },
  selectedOption: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: 'none',
    minHeight: 'inherit', // Ensure consistent height
    display: 'flex',
    alignItems: 'center',
    '&:active, &:hover': {
      transform: 'scale(1)',
    },
  },
  menuToggle: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: 'none',
    minHeight: 'inherit', // Ensure consistent height
    minWidth: 0,
    padding: theme.spacing(0, 1, 0, 2), // Rely on flex to get proper height
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active, &:hover': {
      transform: 'scale(1)',
    },
  },
  menu: {
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
  },
  menuItem: {
    '&:after': {
      borderRadius: 0,
    },
  },
}));

const getStorageKey = (id: string) => `splitbutton-${id}-selected`;

const SplitButtonOption = ({ children }: SplitButtonOptionProps) => {
  // This component doesn't render anything itself - it's just a way to pass props to the parent
  return <>{children}</>;
};

// Type guard to check if a React element has the required SplitButtonOptionProps
const isSplitButtonOptionElement = (
  child: unknown,
): child is ReactElement<SplitButtonOptionProps> => {
  if (!isValidElement(child)) {
    return false;
  }

  // Check if it's a SplitButtonOption by checking the component type
  if (child.type === SplitButtonOption) {
    return true;
  }

  // For backwards compatibility: check if it has the minimum required props
  // (string children, and either onClick or type+value for submit buttons)
  return (
    typeof child.props === 'object' &&
    child.props !== null &&
    'children' in child.props &&
    typeof child.props.children === 'string' &&
    ('onClick' in child.props ||
      ('type' in child.props && 'value' in child.props))
  );
};

// Helper function to extract options from children
const extractOptionsFromChildren = (
  children:
    | ReactElement<SplitButtonOptionProps>
    | ReactElement<SplitButtonOptionProps>[],
): OptionProps[] => {
  const childArray = Children.toArray(children);
  return childArray.filter(isSplitButtonOptionElement).map((child, index) => ({
    label:
      typeof child.props.children === 'string'
        ? child.props.children
        : `Option ${index + 1}`,
    onClick: child.props.onClick,
    disabled: child.props.disabled,
    tooltip: child.props.tooltip,
    description: child.props.description,
    value: child.props.value,
    type: child.props.type,
  }));
};

const SplitButtonBase = (props: SplitButtonProps) => {
  const {
    children,
    id,
    defaultSelectedIndex = 0,
    disabled = false,
    ariaLabel = 'split button',
    className,
    ...buttonProps
  } = props;
  const { startIcon, endIcon, type, ...sharedButtonProps } = buttonProps;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const open = Boolean(anchorEl);

  const [selectedIndex, setSelectedIndex] = useLocalStorage(
    getStorageKey(id),
    defaultSelectedIndex,
  );
  const options = children ? extractOptionsFromChildren(children) : [];

  const handleOptionClick = (index: number) => {
    const selectedOption = options[index];
    if (selectedOption && !selectedOption.disabled) {
      selectedOption.onClick?.();
    }
  };

  const handleClick = () => {
    const selectedOption = options[selectedIndex];
    // For submit buttons, let the browser handle the form submission
    if (selectedOption?.type === 'submit' || selectedOption?.type === 'reset') {
      return;
    }
    // For regular buttons, call the onClick handler
    handleOptionClick(selectedIndex);
  };

  const handleMenuItemClick = (
    _event: MouseEvent<HTMLLIElement>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setAnchorEl(null);
  };

  const handleToggle = (event: MouseEvent<Element>) => {
    setAnchorEl(el => (el ? null : event.currentTarget));
  };

  const handleClose = (event: MouseEvent<Document>) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }

    setAnchorEl(null);
  };

  if (options.length === 0) {
    return null;
  }

  const selectedOption = options[selectedIndex] ?? options[0];
  const menuId = id ? `split-button-menu-${id}` : 'split-button-menu';

  return (
    <>
      <div
        className={classNames(classes.root, className)}
        aria-label={ariaLabel}
      >
        <Tooltip title={selectedOption?.tooltip || ''} arrow placement="top">
          <span>
            <Button
              {...buttonProps}
              className={classes.selectedOption}
              onClick={handleClick}
              disabled={disabled || selectedOption.disabled}
              type={selectedOption.type || buttonProps.type}
              value={selectedOption.value}
            >
              {selectedOption.label}
            </Button>
          </span>
        </Tooltip>
        <Button
          {...sharedButtonProps}
          className={classes.menuToggle}
          aria-controls={open ? menuId : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select action"
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled || options.length <= 1}
          startIcon={<ArrowDropDownIcon />}
        />
      </div>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        keepMounted
        disablePortal
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.label}
            className={classes.menuItem}
            selected={index === selectedIndex}
            onClick={event => handleMenuItemClick(event, index)}
          >
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
              primary={option.label}
              secondary={option.description}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default Object.assign(SplitButtonBase, {
  Option: SplitButtonOption,
});
